import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { ListingPhoto } from "@/components/PhotoPlaceholder";
import BestOfferBadge from "@/components/BestOfferBadge";
import VerifiedExplainer from "@/components/VerifiedExplainer";
import { formatGBP, conditionLabel } from "@/lib/marketplace";
import { Store, ShieldCheck, Star, Tag } from "lucide-react";
import { StoreRatingBadge, StoreReviewsSection } from "@/components/StoreRating";

export default function PublicShop() {
  const { kidId } = useParams();
  const [shop, setShop] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/shop/${kidId}`);
        setShop(data);
      } catch (e) {
        setErr(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [kidId]);

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-20">
        <div className="pill bg-brandPink/15 text-brandPink"><Store className="w-3.5 h-3.5 mr-1" /> Kid shopfront</div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold text-brandSlate mt-3" data-testid="public-shop-title">
          {loading
            ? "Loading shop…"
            : shop?.kid_name
              ? `${shop.kid_name}'s shopfront`
              : "This shopfront"}
        </h1>
        {shop?.kid_id && (
          <div className="mt-2 flex items-center gap-3 flex-wrap" data-testid="public-shop-rating-row">
            {shop.handle && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brandTeal/10 text-brandTealDark font-mono font-bold text-xs">
                @{shop.handle}
              </span>
            )}
            <StoreRatingBadge kidId={shop.kid_id} size={14} />
          </div>
        )}
        <p className="text-slate-600 mt-2 max-w-2xl">
          Every listing is <strong>parent-approved</strong> and safety-checked. Shop with confidence and support young sellers.
        </p>

        {err && (
          <div className="mt-6 card-base p-6 text-rose-700" data-testid="public-shop-error">{err}</div>
        )}

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="public-shop-grid">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="card-base p-4 animate-pulse">
                  <div className="h-48 bg-slate-100 rounded-2xl mb-3" />
                  <div className="h-4 w-3/4 bg-slate-100 rounded" />
                </div>
              ))
            : (shop?.listings || []).length === 0
              ? (
                <div className="col-span-full card-base p-10 text-center text-slate-500" data-testid="public-shop-empty">
                  No live items in this shop right now. Come back soon!
                </div>
              )
              : shop.listings.map((it) => (
                <Link
                  key={it.id}
                  to={`/listing/${it.id}`}
                  className="card-base overflow-hidden block hover:-translate-y-1 hover:shadow-pop transition-all"
                  data-testid={`public-shop-listing-${it.id}`}
                >
                  <div className="aspect-square bg-slate-100 relative">
                    <ListingPhoto src={it.photos?.[0]} alt={it.title} className="w-full h-full object-cover" />
                    {it.is_premium && (
                      <span className="absolute top-2 left-2 pill bg-amber-400 text-amber-950 shadow-soft"><Star className="w-3 h-3 mr-1 fill-amber-900" /> Featured</span>
                    )}
                    {it.pay_what_you_can && (
                      <div className="absolute top-2 right-2">
                        <BestOfferBadge size="sm" testId={`public-shop-best-offer-${it.id}`} />
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      {it.category && <span className="pill bg-amber-100 text-amber-800">{it.category.replace("_", " ")}</span>}
                      <span className="pill bg-slate-100 text-slate-700">{conditionLabel(it.condition)}</span>
                    </div>
                    <div className="font-display text-lg font-medium text-slate-900 line-clamp-1">{it.title}</div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-2xl font-display font-semibold text-brandTeal">{formatGBP(it.price)}</div>
                      <div className="text-xs text-slate-500 inline-flex items-center gap-1" title="Adult Verified Parent"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Adult Verified <VerifiedExplainer testId="adult-verified-explainer-shop" /></div>
                    </div>
                    {it.kid_alias && (
                      <div className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1">
                        <Tag className="w-3 h-3" /> Sold by {it.kid_alias}
                      </div>
                    )}
                  </div>
                </Link>
              ))
          }
        </div>

        <div className="mt-10 text-center">
          <Link to="/browse" className="btn-secondary" data-testid="public-shop-browse-all">
            Browse the whole marketplace
          </Link>
        </div>

        {shop?.kid_id && <StoreReviewsSection kidId={shop.kid_id} />}
      </section>
      <Footer />
    </div>
  );
}
